import type { LucideIcon } from 'lucide-react';
import { type CSSProperties, forwardRef, useId } from 'react';

import { useIsBrandedIcons } from '../hooks/use-is-branded-icons';
import {
  BRANDED_GRID_SCALE,
  resolveBrandedStrokeWidth,
} from '../icons/heidi-product-glyphs/branded-stroke';
import { hasA11yProp, mergeClasses } from '../icons/heidi-product-glyphs/make-branded';

import {
  BRANDED_THUMB_GEOMETRY,
  LUCIDE_THUMB_GEOMETRY,
} from './feedback-thumbs.geometry';
import type { FeedbackThumbsProps } from './feedback-thumbs.types';

const LUCIDE_DEFAULT_STROKE_WIDTH = 2;

// Lucide's rule, so flag-off matches the outline's weight, except unit strings
// fall back to the defaults rather than emit NaN.
function resolveLucideStrokeWidth({
  size,
  strokeWidth = LUCIDE_DEFAULT_STROKE_WIDTH,
  absoluteStrokeWidth,
}: Pick<FeedbackThumbsProps, 'size' | 'strokeWidth' | 'absoluteStrokeWidth'>) {
  if (!absoluteStrokeWidth) return strokeWidth;
  const numericSize = Number(size ?? 24);
  const numericStroke = Number(strokeWidth);
  const stroke = Number.isFinite(numericStroke)
    ? numericStroke
    : LUCIDE_DEFAULT_STROKE_WIDTH;
  return (stroke * 24) / (Number.isFinite(numericSize) ? numericSize : 24);
}

// The cuff/hand division is a masked gap, not a painted line, so it holds on
// any background.
function createFilledThumb(name: string, rotated: boolean): LucideIcon {
  const nameClasses = mergeClasses(
    'lucide',
    `lucide-${name}`,
    rotated ? 'rotate-180' : ''
  );

  const FilledThumb = forwardRef<SVGSVGElement, FeedbackThumbsProps>(
    function FilledThumb(props, ref) {
      const branded = useIsBrandedIcons();
      // React ids contain `:`, unsafe inside `url(#…)`.
      const maskId = `feedback-thumbs-gap-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

      const {
        size,
        strokeWidth,
        absoluteStrokeWidth,
        color,
        className,
        style,
        children,
        ...rest
      } = props;

      const { grid, paths, gap } = branded
        ? BRANDED_THUMB_GEOMETRY
        : LUCIDE_THUMB_GEOMETRY;
      const resolvedStrokeWidth = branded
        ? resolveBrandedStrokeWidth({ size, strokeWidth, absoluteStrokeWidth })
        : resolveLucideStrokeWidth({ size, strokeWidth, absoluteStrokeWidth });
      const paint = color ?? 'currentColor';

      const a11yHidden =
        !children && !hasA11yProp(rest) ? { 'aria-hidden': 'true' as const } : undefined;

      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size ?? 24}
          height={size ?? 24}
          viewBox={`0 0 ${grid} ${grid}`}
          fill={paint}
          stroke={paint}
          strokeWidth={resolvedStrokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={mergeClasses(nameClasses, className)}
          style={
            branded
              ? ({
                  ...style,
                  '--branded-grid-scale': BRANDED_GRID_SCALE,
                } as CSSProperties)
              : style
          }
          {...a11yHidden}
          ref={ref}
          {...rest}
        >
          {/* userSpaceOnUse: the default bbox region clips the stroke. Children
              set their own stroke so a Button's `stroke-icon-*` can't widen the gap. */}
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            x={0}
            y={0}
            width={grid}
            height={grid}
          >
            <rect width={grid} height={grid} fill="white" stroke="none" />
            <line
              x1={gap.x}
              y1={gap.y1}
              x2={gap.x}
              y2={gap.y2}
              stroke="black"
              strokeWidth={gap.width}
              strokeLinecap="butt"
            />
          </mask>
          <g mask={`url(#${maskId})`}>
            {paths.map((d, index) => (
              <path key={index} d={d} />
            ))}
          </g>
          {children}
        </svg>
      );
    }
  );
  FilledThumb.displayName = `Filled(${name})`;
  return FilledThumb;
}

export const ThumbsUpFilled = createFilledThumb('thumbs-up', false);

// Both kits draw thumbs-down as thumbs-up rotated 180°.
export const ThumbsDownFilled = createFilledThumb('thumbs-down', true);
